import React, { useState } from "react";
import { Outlet, NavLink, Link, useLocation } from "react-router-dom";
import { LogOut, Menu, X, ChevronRight } from "lucide-react";
import { useHQAuth } from "@/hq/HQAuthContext";
import { visibleNav, pageTitle } from "@/hq/navConfig";
import HQBrand from "@/hq/HQBrand";
import { TestModeProvider, TestModeBanner } from "@/hq/TestModeContext";
import "@/hq/hq-forms.css";

const ROLE_LABEL = { owner: "OrangeBySite Owner", admin: "OrangeBySite Admin", sales: "OrangeBySite Sales" };

function Sidebar({ onNavigate }) {
  const { admin, logout } = useHQAuth();
  const role = (admin?.role || "admin").toLowerCase();
  const displayName = admin?.full_name || ROLE_LABEL[role] || "OrangeBySite";
  const initial = ((admin?.full_name || admin?.username || admin?.email || "?")[0] || "?").toUpperCase();
  return (
    <aside className="w-[260px] shrink-0 bg-[#0A0A0A] text-white flex flex-col min-h-screen">
      <div className="px-6 pt-7 pb-8">
        <Link to="/hq/dashboard" onClick={onNavigate} data-testid="hq-sidebar-logo" className="inline-flex">
          <HQBrand theme="dark" size="md" testId="hq-sidebar-brand" />
        </Link>
        <div className="mt-3 text-[10px] font-bold tracking-[0.28em] uppercase text-[#8A8A8A]">
          Control Centre
        </div>
      </div>
      <nav className="flex-1 px-3 space-y-1">
        {visibleNav(admin?.role).map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            end={to === "/hq/dashboard"}
            onClick={onNavigate}
            data-testid={`hq-nav-${to.replace(/^\/hq\//, "").replace(/\//g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-3 rounded-xl text-[13px] font-bold tracking-[0.12em] uppercase transition-colors ${
                isActive
                  ? "bg-[#FF6A00] text-black"
                  : "text-[#EAEAEA] hover:bg-white/10 hover:text-white"
              }`
            }
          >
            <Icon size={16} strokeWidth={2.4} />
            {label}
          </NavLink>
        ))}
      </nav>
      <div className="px-4 py-5 border-t border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-[#FF6A00] text-black grid place-items-center font-black text-sm">
            {initial}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[13px] font-semibold truncate">{displayName}</div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-[#8A8A8A]">{role}</div>
          </div>
          <button
            onClick={logout}
            aria-label="Log out"
            title="Log out"
            data-testid="hq-logout"
            className="p-2 rounded-lg hover:bg-white/10 transition-colors"
          >
            <LogOut size={16} />
          </button>
        </div>
        <a
          href="/"
          className="mt-4 flex items-center gap-1.5 text-[10px] tracking-[0.28em] uppercase text-[#8A8A8A] hover:text-white"
        >
          Public site <ChevronRight size={12} />
        </a>
      </div>
    </aside>
  );
}

export default function HQLayout() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const title = pageTitle(location.pathname);
  const { logout } = useHQAuth();

  return (
    <TestModeProvider>
    <div data-hq-scope="true" className="min-h-screen bg-[#FFFDF6] text-[#0A0A0A]" style={{ fontFamily: "'Manrope', ui-sans-serif, system-ui, -apple-system, sans-serif" }}>
      <TestModeBanner />
      {/* Desktop sidebar */}
      <div className="hidden lg:flex fixed inset-y-0 left-0 z-30">
        <Sidebar />
      </div>

      {/* Mobile sidebar drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-[280px] shadow-2xl">
            <button
              onClick={() => setOpen(false)}
              className="absolute top-3 right-3 z-10 p-2 rounded-lg text-white hover:bg-white/10"
              aria-label="Close menu"
              data-testid="hq-drawer-close"
            >
              <X size={18} />
            </button>
            <Sidebar onNavigate={() => setOpen(false)} />
          </div>
        </div>
      )}

      <div className="lg:pl-[260px]">
        <header className="sticky top-0 z-20 bg-white/95 backdrop-blur border-b border-[#EAEAEA]">
          <div className="flex items-center gap-3 px-4 lg:px-8 h-16">
            <button
              onClick={() => setOpen(true)}
              className="lg:hidden p-2 rounded-lg hover:bg-black/5"
              aria-label="Open menu"
              data-testid="hq-menu-open"
            >
              <Menu size={20} />
            </button>
            <div className="min-w-0 flex-1 flex items-center gap-4">
              <Link to="/hq/dashboard" className="hidden lg:inline-flex shrink-0" data-testid="hq-header-brand-wrap">
                <HQBrand theme="light" size="sm" testId="hq-header-brand" />
              </Link>
              <div className="hidden lg:block w-px h-6 bg-[#EAEAEA]" aria-hidden />
              <h1 className="text-[18px] md:text-[20px] font-black tracking-[-0.01em] truncate" data-testid="hq-page-title">
                {title}
              </h1>
            </div>
            {/* Mobile-only logout icon so touch users still have a compact way out.
                Desktop logout lives in the sidebar footer per the brand cleanup. */}
            <button
              onClick={logout}
              data-testid="hq-header-signout-mobile"
              aria-label="Sign out"
              className="lg:hidden p-2 rounded-lg hover:bg-black/5"
            >
              <LogOut size={18} />
            </button>
          </div>
        </header>

        <main className="px-4 lg:px-8 py-6 md:py-8">
          <Outlet />
        </main>
      </div>
    </div>
    </TestModeProvider>
  );
}
